/**
 * The item drawer's properties as a labelled fact grid (the session drawer's FACT_GRID): a tiny
 * dim label over each value. Values read as text at rest and become a control on click: a menu
 * for a choice (status, priority, parent, kind), a text box for typed values (owner, estimate,
 * labels, components). Empty reads as a dim word, never as a placeholder that looks like data.
 */
import { ChevronDown, Plus } from '@orgus/ui/icons';
import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuRadioGroup, DropdownMenuRadioItem, DropdownMenuTrigger, Input, Pressable, Textarea, Tooltip, cn } from '@orgus/ui';
import { FACT_CELL } from '../sessions/SessionFacts.tsx';

/**
 * A value that opens its control: quiet at rest, a soft surface and a chevron on hover or focus.
 * Shifted (not pulled by a margin) 8 px left, so its text lines up with the label and the cell
 * still sizes to the whole value.
 */
export const FACT_VALUE = 'group/value -translate-x-2 flex h-control-sm w-auto max-w-full min-w-0 items-center gap-1 rounded-control px-2 text-ui text-fg-muted hover:bg-hover hover:text-fg focus-visible:bg-hover aria-expanded:bg-hover';

/** An unset value at rest: a quiet dash, never a word that reads like a set value. */
export const EMPTY_DASH = '–';

/** What a screen reader hears for a value: the dash says nothing, so it reads as not set. */
const spoken = (value: string, empty: string) => value || (empty === EMPTY_DASH ? 'not set' : empty);

export function Fact({ label, children, sub, className }: { label: string; children: ReactNode; sub?: ReactNode; className?: string }) {
  return (
    <div className={cn(FACT_CELL, className)}>
      <dt className="truncate text-caption text-fg-subtle">{label}</dt>
      <dd className="flex min-w-0 flex-col">
        {children}
        {sub != null && <span className="min-w-0 truncate text-caption text-fg-subtle">{sub}</span>}
      </dd>
    </div>
  );
}

export interface FactOption {
  value: string;
  label: string;
  /** Shown before the label, in the menu and on the value. */
  icon?: ReactNode;
}

/** A choice: the current value; a click opens a menu of the options. */
/**
 * fit: the value is a short word that must never truncate (a status, a priority): the cell sizes
 * to it. Without it the value truncates (free text such as a parent's title) and `tooltip`
 * carries the whole of it.
 * covers: the cell has a caption under the value (the status's why): the menu opens flush on
 * the value, so it covers the caption whole instead of letting a strip of it show through a gap.
 */
export function FactMenu({ id, label, value, options, display, onChange, wide, fit, covers, tooltip }: { id?: string; label: string; value: string; options: readonly FactOption[]; display?: ReactNode; onChange: (v: string) => void; wide?: boolean; fit?: boolean; covers?: boolean; tooltip?: string }) {
  const current = options.find((o) => o.value === value);
  const trigger = (
    <DropdownMenuTrigger asChild>
      <Pressable id={id} className={cn(FACT_VALUE, fit && 'max-w-none shrink-0')} aria-label={`${label}: ${current?.label ?? 'None'}. Change it`}>
        <span className={cn('flex items-center gap-2', fit ? 'shrink-0 whitespace-nowrap' : 'min-w-0 truncate')}>{display ?? current?.label ?? 'None'}</span>
        <ChevronDown className="size-3 shrink-0 text-fg-subtle opacity-0 transition-opacity duration-(--duration-fast) group-hover/value:opacity-100 group-focus-visible/value:opacity-100 group-aria-expanded/value:opacity-100" aria-hidden />
      </Pressable>
    </DropdownMenuTrigger>
  );
  return (
    <DropdownMenu>
      {tooltip ? <Tooltip content={tooltip}>{trigger}</Tooltip> : trigger}
      <DropdownMenuContent align="start" sideOffset={covers ? 0 : undefined} className={cn('max-h-80 overflow-y-auto', wide ? 'max-w-96' : undefined)} aria-label={label}>
        <DropdownMenuRadioGroup value={value} onValueChange={(v) => v !== value && onChange(v)}>
          {options.map((o) => (
            <DropdownMenuRadioItem key={o.value || 'none'} value={o.value} className="gap-2">
              {o.icon}
              <span className="min-w-0 truncate">{o.label}</span>
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/**
 * An empty value: a dim word at rest ("None"), "Add" while the value is hovered or focused, so
 * empty never reads as data and the way to fill it shows where the pointer is.
 */
export function EmptyValue({ text, add }: { text: string; add?: string }) {
  if (!add) return <span className="min-w-0 truncate text-fg-subtle">{text}</span>;
  // Both words share one grid cell, so the value keeps its width when the word changes.
  return (
    <span className="inline-grid min-w-0">
      <span className="col-start-1 row-start-1 truncate text-fg-subtle group-hover/value:invisible group-focus-visible/value:invisible">{text}</span>
      <span className="invisible col-start-1 row-start-1 flex min-w-0 items-center gap-1 truncate text-fg-muted group-hover/value:visible group-focus-visible/value:visible">
        <Plus className="size-3 shrink-0 text-fg-subtle" aria-hidden />
        {add}
      </span>
    </span>
  );
}

/**
 * A typed value: text at rest (a dim word when empty), a text box on click that saves on Enter
 * or when focus leaves, and goes back unchanged on Escape.
 */
export function FactText({ label, value, empty, addLabel, onSave, inputMode, trailing, maxLength = 100, clean, editing: editingProp, onEditingChange, display }: {
  label: string;
  value: string;
  /** What no value shows: EMPTY_DASH, or a dim word ("Not said"). */
  empty: string;
  /** What hovering an empty value offers: "Add owner". */
  addLabel?: string;
  /** Called only when the value changed. */
  onSave: (v: string) => void;
  inputMode?: 'numeric' | 'text';
  trailing?: ReactNode;
  maxLength?: number;
  /** Filters what is typed (digits only for an estimate). */
  clean?: (v: string) => string;
  editing?: boolean;
  onEditingChange?: (v: boolean) => void;
  /** How a set value reads at rest (tags), instead of the plain text. */
  display?: ReactNode;
}) {
  const [own, setOwn] = useState(false);
  const editing = editingProp ?? own;
  const setEditing = (v: boolean) => (onEditingChange ? onEditingChange(v) : setOwn(v));
  const [draft, setDraft] = useState(value);
  useEffect(() => {
    if (editing) setDraft(value);
    // Start from the saved value each time the box opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing]);
  if (editing) {
    const commit = () => {
      setEditing(false);
      if (draft.trim() !== value) onSave(draft.trim());
    };
    return (
      <Input
        aria-label={label}
        size="sm"
        autoFocus
        inputMode={inputMode}
        value={draft}
        maxLength={maxLength}
        onChange={(e) => setDraft(clean ? clean(e.target.value) : e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') (e.currentTarget as HTMLInputElement).blur();
          else if (e.key === 'Escape') {
            e.preventDefault();
            e.stopPropagation();
            setDraft(value);
            setEditing(false);
          }
        }}
        trailing={trailing}
        wrapperClassName="-translate-x-2 w-40 max-w-full"
      />
    );
  }
  return (
    // A typed value never truncates: a long one wraps inside its cell.
    <Pressable className={cn(FACT_VALUE, 'h-auto min-h-control-sm')} aria-label={`${label}: ${spoken(value, empty)}. Change it`} onClick={() => setEditing(true)}>
      {value ? display ?? <span className="min-w-0 break-words">{value}</span> : <EmptyValue text={empty} add={addLabel} />}
    </Pressable>
  );
}

/**
 * A fact that only shows once it has a value (labels, components). Without one, the cell keeps
 * its place but stays out of sight until the facts are hovered or focused, then offers "Add".
 */
export function OptionalFact({ label, set, children, onAdd, addLabel, editing, className }: { label: string; set: boolean; children: ReactNode; onAdd: () => void; addLabel: string; editing: boolean; className?: string }) {
  if (set || editing) return <Fact label={label} className={className}>{children}</Fact>;
  return (
    // On a phone (no hover) an empty cell would hold a blank row: it leaves, and More actions adds it.
    <div className={cn(FACT_CELL, className, 'max-sm:hidden opacity-0 transition-opacity duration-(--duration-fast) group-hover/facts:opacity-100 focus-within:opacity-100')}>
      <dt className="truncate text-caption text-fg-subtle">{label}</dt>
      <dd>
        <Pressable className={cn(FACT_VALUE, 'text-fg-muted')} onClick={onAdd} aria-label={addLabel}>
          <Plus className="size-3 shrink-0 text-fg-subtle" aria-hidden />
          <span className="truncate">Add</span>
        </Pressable>
      </dd>
    </div>
  );
}

/**
 * A multi-line value (steps to reproduce, expected, actual): text at rest, a text area on click
 * that saves when focus leaves. Escape puts it back unchanged.
 */
export function FactProse({ label, value, empty, onSave, rows = 3 }: { label: string; value: string; empty: string; onSave: (v: string) => void; rows?: number }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  if (editing) {
    return (
      <Textarea
        aria-label={label}
        rows={rows}
        autoFocus
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => {
          setEditing(false);
          if (draft !== value) onSave(draft);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            e.preventDefault();
            e.stopPropagation();
            setDraft(value);
            setEditing(false);
          }
        }}
      />
    );
  }
  return (
    <Pressable
      className="-mx-2 w-auto whitespace-pre-wrap rounded-control px-2 py-1 text-ui text-fg-muted hover:bg-hover hover:text-fg"
      aria-label={`${label}: ${value || empty}. Change it`}
      onClick={() => {
        setDraft(value);
        setEditing(true);
      }}
    >
      {value.trim() ? value : <span className="text-fg-subtle">{empty}</span>}
    </Pressable>
  );
}
